import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fbIntegrationService, userService } from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import { SearchableSelect } from '../../components/SearchableSelect';
import { formatIST } from '../../utils/dates';
import {
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const ROLE_COLORS = {
  super_admin:     'bg-red-100 text-red-700',
  admin:           'bg-amber-100 text-amber-700',
  dealer:          'bg-blue-100 text-blue-700',
  sales_executive: 'bg-emerald-100 text-emerald-700',
};
const ROLE_LABELS = {
  super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive',
};

// Who switched a page on or off, and when — the latest switch in a line, the
// rest on request. A switch-on also says what its look-back pull brought in.
function SwitchLine({ entry }) {
  const b = entry.backfill;
  let pulled = null;
  if (entry.isActive && b) {
    if (!b.finishedAt) pulled = 'checking the last 30 days for missed leads…';
    else if (b.error) pulled = `missed-lead check failed: ${b.error}`;
    else pulled = `${b.imported} missed lead(s) brought in`;
  }
  return (
    <div>
      {entry.isActive ? 'Enabled' : 'Disabled'} {formatIST(entry.at, 'd MMM yyyy, HH:mm')}
      {entry.byName && <> by <span className="text-gray-600">{entry.byName}</span></>}
      {pulled && <> · {pulled}</>}
    </div>
  );
}

function PageSwitchLog({ log = [] }) {
  const [open, setOpen] = useState(false);
  if (!log.length) return null;
  const newestFirst = [...log].reverse();
  return (
    <div className="mt-1 text-[11px] text-gray-400 space-y-0.5">
      <SwitchLine entry={newestFirst[0]} />
      {newestFirst.length > 1 && (
        <button type="button" onClick={() => setOpen(o => !o)} className="text-blue-600 hover:underline">
          {open ? 'Hide history' : `History (${newestFirst.length})`}
        </button>
      )}
      {open && newestFirst.slice(1).map(e => <SwitchLine key={e._id} entry={e} />)}
    </div>
  );
}

// ── App Status (read-only — the Meta app is managed centrally by the company) ──

function AppStatus() {
  const { data, isLoading } = useQuery('fbSettings', fbIntegrationService.getSettings, { retry: false });
  const settings = data?.data?.data || {};

  if (isLoading) return null;

  if (settings.metaAppConfigured) {
    return (
      <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-700 flex items-center gap-2">
        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
        Facebook app is set up by your company. Click <strong>Connect with Facebook</strong> below to link your pages.
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
      Your company hasn’t set up the Facebook app yet. Ask the company admin to configure it before connecting.
    </div>
  );
}

// ── My Connection ─────────────────────────────────────────────────────────────

function MyConnection({ queryClient }) {
  const { data, isLoading, refetch } = useQuery('fbMyAccount', fbIntegrationService.getMyAccount);
  const { data: usersData } = useQuery(
    'fbRoutableUsers',
    () => userService.getAssignableUsers({ includeSuperAdmin: 'true' }),
    { staleTime: 5 * 60 * 1000 }
  );

  const account = data?.data?.data?.account || null;
  const pages   = data?.data?.data?.pages   || [];
  const allUsers = usersData?.data?.data?.users || usersData?.data?.users || [];

  const connectMutation = useMutation(fbIntegrationService.getAuthUrl, {
    onSuccess: (res) => {
      const authUrl = res.data?.data?.authUrl;
      if (authUrl) window.location.href = authUrl;
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to get auth URL'),
  });

  // Meta's webhook is best-effort — this pulls in anything it didn't deliver.
  // The same sync runs automatically every few minutes; this is the manual
  // "check now" for when someone is watching a page that just went live.
  const syncMutation = useMutation(() => fbIntegrationService.syncLeads(48), {
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Checking Facebook for missed leads…');
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Sync failed'),
  });

  // Pages are read from Facebook only when connecting — this picks up pages
  // added to the FB account since then.
  const refreshPagesMutation = useMutation(fbIntegrationService.refreshPages, {
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Pages refreshed');
      refetch();
      queryClient.invalidateQueries('fbAllAccounts');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to refresh pages'),
  });

  const disconnectMutation = useMutation(fbIntegrationService.disconnect, {
    onSuccess: () => {
      toast.success('Disconnected from Facebook');
      refetch();
      queryClient.invalidateQueries('fbAllAccounts');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to disconnect'),
  });

  const toggleMutation = useMutation(fbIntegrationService.togglePage, {
    onSuccess: (res) => {
      const days = res.data?.data?.backfillDays;
      if (days) {
        toast.success(`Page enabled — bringing in its leads from the last ${days} days`);
        // The pull runs in the background; show its result once it has had time
        setTimeout(() => refetch(), 20000);
      }
      refetch();
    },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const routeMutation = useMutation(
    ({ pageId, routeToUserId }) => fbIntegrationService.routePage(pageId, routeToUserId),
    {
      onSuccess: () => refetch(),
      onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update'),
    }
  );

  if (isLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">My Facebook Connection</h2>
          <p className="text-xs text-gray-500 mt-0.5">Connect your Facebook account to manage your pages</p>
        </div>
        {account ? (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">f</div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{account.fbUserName}</p>
                <p className="text-xs text-green-600">Connected</p>
              </div>
            </div>
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isLoading}
              title="Check Facebook for leads from the last 48 hours that haven't reached the LMS"
              className="btn-outline-primary text-xs px-3 py-1.5"
            >
              {syncMutation.isLoading ? <Spinner size="sm" /> : 'Sync missed leads'}
            </button>
            <button
              onClick={() => refreshPagesMutation.mutate()}
              disabled={refreshPagesMutation.isLoading}
              title="Pick up pages added to this Facebook account since it was connected"
              className="btn-outline-primary text-xs px-3 py-1.5"
            >
              {refreshPagesMutation.isLoading ? <Spinner size="sm" /> : 'Refresh pages'}
            </button>
            <button
              onClick={() => { if (window.confirm('Disconnect Facebook account? All page connections will be removed.')) disconnectMutation.mutate(); }}
              disabled={disconnectMutation.isLoading}
              className="btn-outline-secondary text-xs px-3 py-1.5"
            >
              {disconnectMutation.isLoading ? <Spinner size="sm" /> : 'Disconnect'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white text-sm font-semibold rounded-lg hover:bg-[#166FE5] disabled:opacity-50 transition-colors"
          >
            {connectMutation.isLoading ? <Spinner size="sm" /> : (
              <><span className="text-lg leading-none font-bold">f</span> Connect with Facebook</>
            )}
          </button>
        )}
      </div>

      {/* Pages Table */}
      {account && (
        <div>
          {pages.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No Facebook Pages found on this account.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Page Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Route Leads To</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pages.map(page => (
                    <tr key={page._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900">{page.pageName}</td>
                      <td className="px-6 py-3">
                        {page.isActive
                          ? <span className="flex items-center gap-1 text-green-600 text-xs font-semibold"><CheckCircleIcon className="h-4 w-4" />Active</span>
                          : <span className="flex items-center gap-1 text-gray-400 text-xs"><XCircleIcon className="h-4 w-4" />Inactive</span>
                        }
                        <PageSwitchLog log={page.switchLog} />
                      </td>
                      <td className="px-6 py-3">
                        <SearchableSelect
                          value={page.routeToUserId?._id || page.routeToUserId || ''}
                          onChange={val => routeMutation.mutate({ pageId: page.pageId, routeToUserId: val })}
                          options={allUsers.map(u => ({
                            value: u._id,
                            label: `${u.firstName} ${u.lastName} (${ROLE_LABELS[u.role] || u.role})`,
                          }))}
                          allLabel="— Select User —"
                          placeholder="Search user…"
                          buttonClassName="input text-xs py-1.5 w-48"
                        />
                      </td>
                      <td className="px-6 py-3">
                        <button
                          onClick={() => toggleMutation.mutate(page.pageId)}
                          disabled={toggleMutation.isLoading}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                            page.isActive
                              ? 'border-red-200 text-red-600 hover:bg-red-50'
                              : 'border-green-200 text-green-600 hover:bg-green-50'
                          }`}
                        >
                          {page.isActive ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-6 py-3 text-xs text-gray-400 border-t border-gray-100">
                Leads arrive live from Facebook. As a safety net the LMS also checks every few
                minutes for anything Facebook didn't deliver and imports it automatically —
                "Sync missed leads" runs that check on demand. Enabling a page also brings in
                its leads from the last 30 days, so leads that came while it was off aren't
                lost. A lead already in the LMS is never imported twice.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── All Accounts (super_admin only) ──────────────────────────────────────────

function AllAccounts({ queryClient }) {
  const { data, isLoading, refetch } = useQuery('fbAllAccounts', fbIntegrationService.getAllAccounts);
  const accounts = data?.data?.data?.accounts || [];

  const disconnectMutation = useMutation(fbIntegrationService.disconnectAccount, {
    onSuccess: () => { toast.success('Account disconnected'); refetch(); queryClient.invalidateQueries('fbMyAccount'); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (isLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-base font-semibold text-gray-900">All Connected Accounts</h2>
        <p className="text-xs text-gray-500 mt-0.5">Overview of all Facebook accounts connected by your team</p>
      </div>
      {accounts.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-400">No accounts connected yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">LMS User</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">FB Account</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Pages</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Connected</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map(acc => (
                <tr key={acc._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <p className="font-medium text-gray-900">{acc.userId?.firstName} {acc.userId?.lastName}</p>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[acc.userId?.role] || ''}`}>
                      {ROLE_LABELS[acc.userId?.role] || acc.userId?.role}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">f</div>
                      <span className="text-gray-700">{acc.fbUserName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="space-y-0.5">
                      {acc.pages.map(p => (
                        <div key={p._id} className="flex items-center gap-1.5 text-xs text-gray-600">
                          {p.isActive
                            ? <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                            : <span className="h-1.5 w-1.5 rounded-full bg-gray-300 inline-block" />
                          }
                          {p.pageName}
                          {p.routeToUserId && (
                            <span className="text-gray-400">→ {p.routeToUserId.firstName} {p.routeToUserId.lastName}</span>
                          )}
                        </div>
                      ))}
                      {acc.pages.length === 0 && <span className="text-xs text-gray-400">No pages</span>}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-500">
                    {new Date(acc.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      onClick={() => { if (window.confirm(`Disconnect ${acc.userId?.firstName}'s Facebook account?`)) disconnectMutation.mutate(acc._id); }}
                      disabled={disconnectMutation.isLoading}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Disconnect"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Lead Form Health ─────────────────────────────────────────────────
// A mis-worded form question is silent: the leads still arrive, they just
// arrive without a model or without a usable buying time, and nobody notices
// for months. This puts a number on it per form, so a bad form is obvious in
// the same week it goes live.

const HEALTH_STYLES = {
  broken: { badge: 'bg-red-100 text-red-700',         label: 'Answers not read' },
  check:  { badge: 'bg-amber-100 text-amber-700',     label: 'Worth checking' },
  ok:     { badge: 'bg-emerald-100 text-emerald-700', label: 'Healthy' },
};

function FormHealth() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery(
    ['fbFormHealth', days],
    () => fbIntegrationService.getFormHealth(days),
    { keepPreviousData: true },
  );

  const health = data?.data?.data;
  const forms  = health?.forms || [];
  const totals = health?.totals;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="text-lg font-semibold text-gray-900">Lead form health</h2>
          <p className="mt-1 text-sm text-gray-500">
            How much of each form&apos;s information actually reaches your leads. A form
            missing most of its answers is usually asking its questions in wording the
            import doesn&apos;t recognise &mdash; re-word the question on Facebook, or
            send us the form name and we will teach the import to read it.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : !forms.length ? (
        <p className="mt-6 text-sm text-gray-500">No Facebook leads in this period.</p>
      ) : (
        <>
          {totals?.needsReview > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">
                {totals.needsReview} of {totals.forms} form{totals.forms === 1 ? '' : 's'} are losing
                information. In this period {totals.noModel} lead{totals.noModel === 1 ? '' : 's'}{' '}
                arrived with no model and {totals.noBuyingTime} with no usable buying time.
              </p>
            </div>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4">Form</th>
                  <th className="py-2 pr-4">Leads</th>
                  <th className="py-2 pr-4">Missing model</th>
                  <th className="py-2 pr-4">Missing buying time</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {forms.map((f) => {
                  const style = HEALTH_STYLES[f.status] || HEALTH_STYLES.ok;
                  return (
                    <tr key={`${f.formId || 'legacy'}-${f.page}`} className="text-sm">
                      <td className="py-2 pr-4">
                        <div className="font-medium text-gray-900">
                          {f.formName || 'Leads imported before form tracking'}
                        </div>
                        <div className="text-xs text-gray-500">{f.page || 'Page not recorded'}</div>
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{f.leads}</td>
                      <td className="py-2 pr-4 text-gray-700">{f.noModel} ({f.modelMissRate}%)</td>
                      <td className="py-2 pr-4 text-gray-700">{f.noBuyingTime} ({f.buyingMissRate}%)</td>
                      <td className="py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}>
                          {style.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export const FacebookIntegration = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const connected = searchParams.get('connected');
    const error     = searchParams.get('error');
    if (connected === 'true') {
      toast.success('Facebook account connected successfully!');
      queryClient.invalidateQueries('fbMyAccount');
      queryClient.invalidateQueries('fbAllAccounts');
      setSearchParams({});
    }
    if (error) {
      toast.error(`Connection failed: ${decodeURIComponent(error)}`);
      setSearchParams({});
    }
  }, [searchParams]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Facebook Integration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Connect Facebook pages to automatically receive lead form submissions in your LMS.
        </p>
      </div>

      {/* App status — credentials are managed centrally by the company */}
      <AppStatus />

      {/* My Connection */}
      <MyConnection queryClient={queryClient} />

      {/* Lead form health — surfaces a form whose answers stop being read.
          Super admin only (company users act as one inside a workspace). */}
      {user?.role === 'super_admin' && <FormHealth />}

      {/* All Accounts — super_admin only */}
      {user?.role === 'super_admin' && <AllAccounts queryClient={queryClient} />}
    </div>
  );
};
